// Media Gallery Popup functionality
class MediaGalleryPopup {
  constructor() {
    this.popup = document.getElementById('media-gallery-popup');

    // Move the overlay to <body> so its position: fixed and z-index aren't
    // trapped by the containing block/stacking context that #product-media
    // creates via `container-type: inline-size` (used for container queries
    // in media-gallery.css). Otherwise the overlay renders confined to that
    // column, appearing behind the header and sticky add-to-cart bar.
    if (this.popup && this.popup.parentElement !== document.body) {
      document.body.appendChild(this.popup);
    }

    this.popupCloseBtn = document.querySelector('.media-gallery-popup-close');
    this.mainGallery = document.querySelector('media-gallery');
    this.popupGallery = document.querySelector('media-gallery-popup');
    this.mainViewerItems = document.querySelectorAll('.media-gallery:not(.media-gallery--popup) .media-viewer__item');
    this.popupViewerItems = document.querySelectorAll('.media-gallery--popup .media-viewer__item');
    this.popupViewerList = document.getElementById('gallery-viewer-popup');
    this.popupThumbsItems = document.querySelectorAll('.media-gallery--popup .media-thumbs__item');
    this.popupPrevBtn = document.querySelector('.media-gallery--popup .media-ctrl__btn[name="prev"]');
    this.popupNextBtn = document.querySelector('.media-gallery--popup .media-ctrl__btn[name="next"]');
    this.popupCurrentCounter = document.querySelector('.media-gallery--popup .media-ctrl__current-item');
    this.loadingSpinner = document.querySelector('.media-gallery--popup .loading-spinner');
    this.zoomLinks = this.popupGallery.querySelectorAll('.js-zoom-link');
    this.currentIndex = 0;
    this.touchZoomStates = new Map();
    this.zoomResetFns = new Map();

    this.init();
  }

  init() {
    // Add click handlers to main gallery items
    this.mainViewerItems.forEach((item, index) => {
      item.addEventListener('click', () => this.openPopup(index));
    });

    // Add close button handler
    if (this.popupCloseBtn) {
      this.popupCloseBtn.addEventListener('click', () => this.closePopup());
    }

    // Close popup when clicking on overlay
    if (this.popup) {
      this.popup.addEventListener('click', (e) => {
        if (e.target === this.popup) {
          this.closePopup();
        }
      });
    }

    // Navigation buttons
    if (this.popupPrevBtn) {
      this.popupPrevBtn.addEventListener('click', () => this.previousImage());
    }

    if (this.popupNextBtn) {
      this.popupNextBtn.addEventListener('click', () => this.nextImage());
    }

    // Thumbnail navigation
    this.popupThumbsItems.forEach((item, index) => {
      const btn = item.querySelector('.media-thumbs__btn');
      if (btn) {
        btn.addEventListener('click', () => this.goToImage(index));
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.popup.classList.contains('is-active')) return;

      if (e.key === 'Escape') {
        this.closePopup();
      } else if (e.key === 'ArrowLeft') {
        this.previousImage();
      } else if (e.key === 'ArrowRight') {
        this.nextImage();
      }
    });

    // Zoom initialization
    if (this.zoomLinks.length > 0) {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.setupTouchZoom();
    }
  }

  openPopup(index) {
    this.currentIndex = index;
    this.popup.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    this.updatePopupGallery();

    // Initialize zoom for the popup
    if (this.zoomLinks.length > 0) {
      setTimeout(() => {
        this.initZoom();
      }, 100);
    }
  }

  closePopup() {
    this.popup.classList.remove('is-active');
    document.body.style.overflow = '';
    
    // Remove zoom event listeners
    if (this.zoomLinks.length > 0 && this.zoomEventListener) {
      this.zoomLinks.forEach((el) => {
        el.removeEventListener('mousemove', this.zoomEventListener);
        this.resetTouchZoom(el);
      });
    }

    window.pauseAllMedia();
  }

  goToImage(index) {
    this.currentIndex = index;
    this.updatePopupGallery();
  }

  previousImage() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      window.pauseAllMedia();
      this.updatePopupGallery();
    }
  }

  nextImage() {
    if (this.currentIndex < this.popupViewerItems.length - 1) {
      this.currentIndex++;
      window.pauseAllMedia();
      this.updatePopupGallery();
    }
  }

  updatePopupGallery() {
    // Update active classes on viewer items
    this.popupViewerItems.forEach((item, index) => {
      if (index === this.currentIndex) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });

    // Scroll the viewer to show current item using transform
    if (this.popupViewerList) {
      const scrollPosition = -(this.currentIndex * 100) - 50;
      this.popupViewerList.style.transform = `translateX(${scrollPosition}%)`;
    }

    // Update active classes on thumbnail items
    this.popupThumbsItems.forEach((item, index) => {
      const btn = item.querySelector('.media-thumbs__btn');
      if (index === this.currentIndex) {
        item.classList.add('is-active');
        if (btn) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-current', 'true');
        }
        // Scroll thumbnail into view
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      } else {
        item.classList.remove('is-active');
        if (btn) {
          btn.classList.remove('is-active');
          btn.removeAttribute('aria-current');
        }
      }
    });

    // Update counter
    if (this.popupCurrentCounter) {
      this.popupCurrentCounter.textContent = this.currentIndex + 1;
    }

    // Update button disabled states
    if (this.popupPrevBtn) {
      this.popupPrevBtn.disabled = this.currentIndex === 0;
    }

    if (this.popupNextBtn) {
      this.popupNextBtn.disabled = this.currentIndex === this.popupViewerItems.length - 1;
    }

    // Re-initialize zoom for active item, resetting any touch zoom left over
    // from the slide the user just navigated away from.
    if (this.zoomLinks.length > 0) {
      this.zoomLinks.forEach((el) => this.resetTouchZoom(el));
      this.initZoom();
    }
  }

  /**
   * Initialized the zoom on hover for popup gallery
   */
  initZoom() {
    this.zoomLinks.forEach((el) => {

        el.addEventListener('click', (e) => {
          e.preventDefault();
        });

        const zoomImage = el.querySelector('.zoom-image');
        if (!zoomImage) return;

        const zoomWidth = Number(zoomImage.dataset.originalWidth || 0);
        const productImage = el.querySelector('.product-image');
        if (!productImage) return;

        const imageWidth = productImage.getBoundingClientRect().width;
        const hasTouch = theme.device && theme.device.hasTouch;
        if (theme.mediaMatches && theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
            el.addEventListener('mousemove', this.zoomEventListener);
            el.classList.remove('pointer-events-none');
        } else if (hasTouch) {
            // Keep pointer events enabled on touch devices so pinch/double-tap
            // zoom (setupTouchZoom) still reaches this element.
            el.removeEventListener('mousemove', this.zoomEventListener);
            el.classList.remove('pointer-events-none');
        } else {
            el.removeEventListener('mousemove', this.zoomEventListener);
            el.classList.add('pointer-events-none');
        }
    });
  }

  /**
   * Handles mouse move over a zoomable image in popup
   * @param {?object} evt - Event object.
   */
  handleZoomMouseMove(evt) {
    const hoverElem = evt.currentTarget;
    const zoomImage = hoverElem.querySelector('.js-zoom-image');

    if (!zoomImage) return;

    // Download the zoom image if necessary
    if (zoomImage.dataset.src) {
      if (this.loadingSpinner) {
        this.loadingSpinner.classList.remove('loading-spinner--out');
      }

      const img = new Image();
      img.src = zoomImage.dataset.src;
      img.onload = () => {
        zoomImage.src = img.src;
        hoverElem.classList.remove('media--zoom-not-loaded');
        if (this.loadingSpinner) {
          this.loadingSpinner.classList.add('loading-spinner--out');
        }
      };
      zoomImage.removeAttribute('data-src');
    }

    try {
      const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
      const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
      const x = (offsetX / zoomImage.offsetWidth) * 100;
      const y = (offsetY / zoomImage.offsetHeight) * 100;
      zoomImage.style.objectPosition = `${x}% ${y}%`;
    } catch (err) {
      // Fail silently
    }
  }

  /**
   * Pinch-to-zoom and double-tap-to-zoom for touch devices, since hover-based
   * zoom (initZoom/handleZoomMouseMove) has no equivalent on touch.
   */
  setupTouchZoom() {
    const MAX_SCALE = 4;
    const TAP_SCALE = 2.5;
    const DOUBLE_TAP_MS = 300;
    const DOUBLE_TAP_SLOP = 30;

    const getDistance = (touches) => Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    );

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    this.zoomLinks.forEach((el) => {
      const zoomImage = el.querySelector('.zoom-image');
      const productImage = el.querySelector('.product-image');
      if (!zoomImage || !productImage) return;

      const state = {
        scale: 1,
        translateX: 0,
        translateY: 0,
        pinching: false,
        panning: false,
        startDistance: 0,
        startScale: 1,
        panStart: null,
        lastTapTime: 0,
        lastTapPoint: null,
      };
      this.touchZoomStates.set(el, state);

      const loadZoomImage = () => {
        if (!zoomImage.dataset.src) return;
        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          el.classList.remove('media--zoom-not-loaded');
          // The image may have finished loading mid-gesture; reveal it now
          // if we're still zoomed in.
          applyTransform(true);
        };
        zoomImage.removeAttribute('data-src');
      };

      const applyTransform = (animate) => {
        zoomImage.style.transition = animate ? 'transform 0.25s ease, opacity 0.2s ease' : 'none';
        productImage.style.transition = 'opacity 0.2s ease';
        const tx = state.translateX / state.scale;
        const ty = state.translateY / state.scale;
        zoomImage.style.transform = `scale(${state.scale}) translate(${tx}px, ${ty}px)`;
        // Don't crossfade to the hi-res image until it has actually loaded,
        // otherwise a still-loading zoom-in shows the blank placeholder.
        const isZoomed = state.scale > 1.01 && !el.classList.contains('media--zoom-not-loaded');
        zoomImage.style.opacity = isZoomed ? '1' : '0';
        productImage.style.opacity = isZoomed ? '0' : '1';
        el.classList.toggle('is-zoomed', isZoomed);
      };

      const clampTranslate = () => {
        const rect = el.getBoundingClientRect();
        const maxX = (rect.width * (state.scale - 1)) / 2;
        const maxY = (rect.height * (state.scale - 1)) / 2;
        state.translateX = clamp(state.translateX, -maxX, maxX);
        state.translateY = clamp(state.translateY, -maxY, maxY);
      };

      const resetZoom = (animate) => {
        state.scale = 1;
        state.translateX = 0;
        state.translateY = 0;
        applyTransform(animate);
      };
      this.zoomResetFns.set(el, resetZoom);

      el.addEventListener('touchstart', (evt) => {
        if (evt.touches.length === 2) {
          evt.preventDefault();
          state.pinching = true;
          state.panning = false;
          state.startDistance = getDistance(evt.touches);
          state.startScale = state.scale;
          loadZoomImage();
        } else if (evt.touches.length === 1) {
          const touch = evt.touches[0];
          const now = Date.now();
          const point = { x: touch.clientX, y: touch.clientY };

          if (
            state.lastTapTime &&
            now - state.lastTapTime < DOUBLE_TAP_MS &&
            state.lastTapPoint &&
            Math.hypot(point.x - state.lastTapPoint.x, point.y - state.lastTapPoint.y) < DOUBLE_TAP_SLOP
          ) {
            evt.preventDefault();
            state.lastTapTime = 0;
            state.lastTapPoint = null;
            if (state.scale > 1.01) {
              resetZoom(true);
            } else {
              loadZoomImage();
              state.scale = TAP_SCALE;
              state.translateX = 0;
              state.translateY = 0;
              applyTransform(true);
            }
            return;
          }

          state.lastTapTime = now;
          state.lastTapPoint = point;

          if (state.scale > 1.01) {
            evt.preventDefault();
            state.panning = true;
            state.panStart = { x: touch.clientX, y: touch.clientY, translateX: state.translateX, translateY: state.translateY };
          }
        }
      }, { passive: false });

      el.addEventListener('touchmove', (evt) => {
        if (state.pinching && evt.touches.length === 2) {
          evt.preventDefault();
          const distance = getDistance(evt.touches);
          state.scale = clamp(state.startScale * (distance / state.startDistance), 1, MAX_SCALE);
          clampTranslate();
          applyTransform(false);
        } else if (state.panning && evt.touches.length === 1) {
          evt.preventDefault();
          const touch = evt.touches[0];
          state.translateX = state.panStart.translateX + (touch.clientX - state.panStart.x);
          state.translateY = state.panStart.translateY + (touch.clientY - state.panStart.y);
          clampTranslate();
          applyTransform(false);
        }
      }, { passive: false });

      const endTouch = (evt) => {
        // Lifting one finger of a two-finger pinch: keep zoom active and
        // continue panning with the remaining finger instead of ending the
        // gesture outright.
        if (state.pinching && evt.touches && evt.touches.length === 1) {
          state.pinching = false;
          state.panning = true;
          const touch = evt.touches[0];
          state.panStart = { x: touch.clientX, y: touch.clientY, translateX: state.translateX, translateY: state.translateY };
          return;
        }

        if (evt.touches && evt.touches.length > 0) return;

        state.pinching = false;
        state.panning = false;
        if (state.scale <= 1.01) {
          resetZoom(true);
        } else {
          clampTranslate();
          applyTransform(true);
        }
      };

      el.addEventListener('touchend', endTouch);
      el.addEventListener('touchcancel', endTouch);
    });
  }

  /**
   * Resets any pinch/double-tap zoom applied to a zoom link back to 1:1.
   * @param {Element} el - The `.js-zoom-link` element.
   */
  resetTouchZoom(el) {
    if (this.zoomResetFns.has(el)) {
      this.zoomResetFns.get(el)(false);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MediaGalleryPopup();
  });
} else {
  new MediaGalleryPopup();
}
